"use client";

/**
 * The "where does this actually go?" gate, before a user follows an untrusted link.
 *
 * This is the SECURITY half of link previewing, and the half that already
 * shipped. It answers the question a preview card answers — *what is on the
 * other end of this* — for the case where the answer has to be trustworthy
 * rather than pretty: the destination host and the full URL, shown from the
 * PARENT realm, before any navigation happens.
 *
 * WHY IT LIVES HERE. It was written inside `@west-tennessee-consulting/email`'s
 * `SafeEmailBody`, which is where inbound mail needed it. That made it shared
 * in name and unreachable in practice — the same shape as this package's SSRF
 * guard, which sat inside a package called *link-preview* and grew three rival
 * copies elsewhere because nobody thinks to look there for URL validation.
 *
 * Its natural home is beside the rest of the link vocabulary, for one concrete
 * reason: when the OG-card preview lands, the plan is for email previews to
 * render INSIDE this interstitial — turning "are you sure?" into "here is what
 * is there." Co-locating them makes that a local change rather than a
 * cross-package one. `email` now depends on this package; this package must
 * never depend on `email`.
 *
 * CLIENT-SAFE. Imports react/react-dom and nothing else from this package's
 * node-only half (guard, transport, resolver all reach for node:dns /
 * node:crypto). Safe in a browser bundle.
 *
 * WHAT IT DELIBERATELY DOES NOT DO. It does not fetch the destination. Probing
 * a link in inbound mail confirms mailbox liveness to a spammer and trips
 * read-beacons — which is exactly why the email tier of the preview plan is
 * click-to-reveal. Everything here is derived from the href itself.
 */

import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

export { classifyExternalHref, isDirectOpenProtocol, type ExternalLinkTarget } from "../link-target.js";
import type { ExternalLinkTarget } from "../link-target.js";

/**
 * Open an external URL with the opener relationship severed.
 *
 * `noopener` stops the destination scripting our window; `noreferrer` stops it
 * learning the URL it was opened from, which matters because our URLs carry
 * entity ids — a link in a note about a deal would otherwise tell the
 * destination which deal it was.
 */
export function openExternalSafely(href: string): void {
  window.open(href, "_blank", "noopener,noreferrer");
}

/**
 * State for a confirm-before-navigate flow.
 *
 * Bundled as a hook because the three pieces have to agree: what is pending,
 * what confirming does, and the fact that confirming CLEARS the pending link.
 * The clear-on-confirm happens inside the state updater so a double-click
 * cannot open the destination twice.
 */
export function useExternalLinkConfirmation(): {
  pendingLink: ExternalLinkTarget | null;
  requestNavigation: (link: ExternalLinkTarget) => void;
  confirm: () => void;
  cancel: () => void;
} {
  const [pendingLink, setPendingLink] = useState<ExternalLinkTarget | null>(null);

  const requestNavigation = useCallback((link: ExternalLinkTarget) => {
    setPendingLink(link);
  }, []);

  const confirm = useCallback(() => {
    setPendingLink((current) => {
      if (current) openExternalSafely(current.href);
      return null;
    });
  }, []);

  const cancel = useCallback(() => {
    setPendingLink(null);
  }, []);

  return { pendingLink, requestNavigation, confirm, cancel };
}

export interface LinkConfirmDialogProps {
  link: ExternalLinkTarget | null;
  onCancel: () => void;
  onConfirm: () => void;
  /**
   * Heading. Defaults to the inbox wording this was written for, so the email
   * renderer reads identically after extraction; other surfaces should say
   * where the reader is actually leaving.
   */
  title?: string;
  /** Body copy above the destination. Defaults to the inbox wording. */
  description?: string;
}

/**
 * Interstitial shown before following an untrusted link. Surfaces the true
 * destination host + full URL so the user can spot a mismatch — an obfuscated
 * tracking or redirect domain — before leaving.
 *
 * Self-contained by design: portal + inline styles + CSS system colors, with no
 * dependency on any host design system, so it renders identically in every app
 * that adopts it. A security control that looks different per app is one people
 * learn to click through in the app where it looks unfamiliar.
 */
export function LinkConfirmDialog({
  link,
  onCancel,
  onConfirm,
  title = "Leaving your inbox",
  description = "This link goes to an external site. Check the address before continuing — WTC hasn’t verified where it leads.",
}: LinkConfirmDialogProps): React.ReactPortal | null {
  // Escape closes the dialog.
  useEffect(() => {
    if (!link) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [link, onCancel]);

  if (!link || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483647, // z-index-allow: link-confirm security interstitial portalled to body — must render above sandboxed email iframe content and any host-app modal; shared pkg has no host Tailwind z-scale
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        background: "rgba(0,0,0,0.5)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          colorScheme: "light dark",
          width: "100%",
          maxWidth: "440px",
          background: "Canvas",
          color: "CanvasText",
          border: "1px solid color-mix(in srgb, CanvasText 20%, transparent)",
          borderRadius: "12px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
          padding: "20px",
          fontFamily:
            'ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
          <span aria-hidden="true" style={{ fontSize: "18px" }}>
            ⚠️
          </span>
          <h2 style={{ margin: 0, fontSize: "16px", fontWeight: 600 }}>{title}</h2>
        </div>
        <p style={{ margin: "0 0 14px", fontSize: "13px", lineHeight: 1.5, opacity: 0.85 }}>
          {description}
        </p>

        {link.host && (
          <div style={{ marginBottom: "10px" }}>
            <div style={labelStyle}>Destination</div>
            <div style={{ fontSize: "14px", fontWeight: 600, wordBreak: "break-all" }}>
              {link.host}
            </div>
          </div>
        )}
        <div style={{ marginBottom: "18px" }}>
          <div style={labelStyle}>Full link</div>
          <div
            style={{
              maxHeight: "8rem",
              overflowY: "auto",
              background: "color-mix(in srgb, CanvasText 8%, transparent)",
              borderRadius: "6px",
              padding: "8px",
              fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace",
              fontSize: "12px",
              wordBreak: "break-all",
            }}
          >
            {link.href}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
          <button type="button" onClick={onCancel} style={secondaryButtonStyle}>
            Cancel
          </button>
          <button type="button" onClick={onConfirm} style={primaryButtonStyle}>
            Continue to site ↗
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: "11px",
  fontWeight: 500,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  opacity: 0.6,
  marginBottom: "2px",
};

const baseButtonStyle: React.CSSProperties = {
  fontSize: "13px",
  fontWeight: 500,
  padding: "8px 14px",
  borderRadius: "8px",
  cursor: "pointer",
};

const secondaryButtonStyle: React.CSSProperties = {
  ...baseButtonStyle,
  background: "transparent",
  color: "CanvasText",
  border: "1px solid color-mix(in srgb, CanvasText 30%, transparent)",
};

const primaryButtonStyle: React.CSSProperties = {
  ...baseButtonStyle,
  background: "#245397",
  color: "#ffffff",
  border: "1px solid #245397",
};
